<template>
  <div class="wrapper">
    <div class="top-right">
      <slot name="top-right"></slot>
    </div>
    <svg class="line-chart" :viewBox="viewBox" v-show="hasData">
      <g :transform="chartTransform">
        <transition name="line">
          <path class="line-chart__line path" :d="line" v-if="isShowLine" />
        </transition>
        <g ref="yAxis" class="axis"></g>
        <g ref="xAxis" class="axis" :transform="xAxisTransform"></g>
        <g v-for="point in data" :key="point[xField] + point[yField]">
          <circle
            class="line-chart__circle"
            r="4"
            :cx="xScale(point[xField])"
            :cy="yScale(point[yField])"
            @mouseover="
              onMouseover($event, {
                cx: xScale(point[xField]),
                cy: yScale(point[yField]),
                value: point[yField]
              })
            "
            @mouseout="onMouseout"
            :key="point[yField] + new Date()"
          ></circle>
        </g>
      </g>
      <svg
        ref="tooltip"
        class="line-chart__tooltip hide"
        width="52"
        height="30"
        viewBox="0 0 52 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M39.3514 0H12.6486C5.66278 0 0 5.94056 0 13.2682C0
            20.5959 5.66278 26.5365 12.6486 26.5365H22.8737L25.674
             30L28.4747 26.5365H39.3514C46.3369 26.5365 52 20.5959
              52 13.2682C52 5.94056 46.3369 0 39.3514 0Z"
          fill="#3E77C7"
        />
        <text
          ref="tooltipValue"
          fill="white"
          xml:space="preserve"
          font-size="8"
          dominant-baseline="middle"
          text-anchor="middle"
          x="50%"
          y="50%"
        ></text>
      </svg>
    </svg>
    <NoDataState v-if="!hasData"></NoDataState>
  </div>
</template>

<script>
import * as d3 from 'd3';
import NoDataState from '@/components/statistics/NoDataState.vue';
import { formatDate } from '@/helpers/date';

const today = formatDate(new Date());

export default {
  name: 'LineChart',
  components: {
    NoDataState,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    width: {
      default: 500,
      type: Number,
    },
    height: {
      default: 200,
      type: Number,
    },
    xField: {
      default: 'date',
      type: String,
    },
    yField: {
      default: 'value',
      type: String,
    },
    xFieldDateFormat: {
      default: '',
      type: String,
    },
    prependYaxis: {
      default: '',
      type: String,
    },
  },
  data() {
    return {
      padding: 60,
      margin: {
        top: 40,
        left: 40,
        bottom: 40,
        right: 0,
      },
      isShowLine: false,
    };
  },
  computed: {
    chartTransform() {
      return `translate(${this.margin.left}, ${this.margin.top})`;
    },
    xAxisTransform() {
      return `translate(0, ${this.height - this.padding})`;
    },
    rangeX() {
      const width = this.width - this.padding - this.margin.left;
      return [0, width];
    },
    rangeY() {
      const height = this.height - this.padding;
      return [height, 0];
    },
    xScale() {
      return d3
        .scaleTime()
        .range(this.rangeX)
        .domain(d3.extent(this.data, (d) => d[this.xField]))
        .rangeRound([
          this.margin.left,
          this.width - this.margin.left - this.padding - this.margin.right,
        ]);
    },
    yScale() {
      return d3
        .scaleLinear()
        .range(this.rangeY)
        .domain([0, d3.max(this.data, (d) => d[this.yField])]);
    },
    xAxis() {
      const xAxis = d3
        .axisBottom(this.xScale)
        .tickFormat((x) => {
          switch (this.xFieldDateFormat) {
            case 'year':
              return d3.timeFormat('%Y')(x);
            case 'month':
              return d3.timeFormat('%b')(x);
            case 'week':
              if (d3.timeFormat('%Y-%m-%d')(x) === today) {
                return '今天';
              }
              return d3.timeFormat('%Y-%m-%d')(x);
            default:
              return d3.timeFormat('%Y-%m-%d')(x);
          }
        });

      if (this.data.length < 10) {
        return xAxis.tickValues(this.data.map((d) => new Date(d.date)));
      } return xAxis.ticks(Math.min(this.data.length <= 2 ? 1 : 5, this.data.length));
    },
    yAxis() {
      return d3
        .axisLeft(this.yScale)
        .tickSizeInner(-420)
        .tickFormat((y) => `${this.prependYaxis}${y}`)
        .ticks(
          Math.min(
            10,
            d3.max(this.data, (d) => d[this.yField]),
          ) + 1,
        );
    },
    line() {
      return d3
        .line()
        .x((d) => this.xScale(d[this.xField]))
        .y((d) => this.yScale(d[this.yField]))
        .curve(d3.curveBumpX)(this.data);
    },
    viewBox() {
      return `0 0 ${this.width} ${this.height}`;
    },
    chartWidth() {
      return this.width - (this.margin.left + this.margin.right);
    },
    chartHeight() {
      return this.height - (this.margin.top + this.margin.bottom);
    },
    hasData() {
      return this.data.some((data) => data.value > 0);
    },
  },
  beforeUpdate() {
    this.drawAxis();
  },
  methods: {
    drawAxis() {
      d3.select(this.$refs.yAxis)
        .transition()
        .duration(500)
        .call(this.yAxis);
      d3.select(this.$refs.xAxis)
        .transition()
        .duration(500)
        .call(this.xAxis);
    },
    onMouseover(event, { cx, cy, value }) {
      this.$refs.tooltip.classList.remove('hide');
      this.$refs.tooltip.setAttribute('x', cx + 14);
      this.$refs.tooltip.setAttribute('y', cy);
      if (Math.floor(value) > 1e9) {
        value = new Intl.NumberFormat('zh-HK', {
          notation: 'compact',
        }).format(value);
      }
      this.$refs.tooltipValue.textContent = `${this.prependYaxis}${value}`;
    },
    onMouseout() {
      this.$refs.tooltip.classList.add('hide');
    },
    toggleIsShowLine() {
      this.isShowLine = !this.isShowLine;
    },
  },
  mounted() {
    this.drawAxis();
    this.toggleIsShowLine();
  },
  watch: {
    line() {
      this.toggleIsShowLine();
      setTimeout(() => {
        this.toggleIsShowLine();
      }, 0);
    },
  },
};
</script>

<style lang="scss" scoped>
$main-color: #3e77c7;
$stroke-color: #3b71b8;

.top-right {
  position: absolute;
  right: 2%;
  top: 5%;
  z-index: 10;
}

div {
  position: relative;
}

.wrapper {
  padding: 1rem;
  background-color: #fff;
  border: #dde1e5 1px solid;
  border-radius: 8px;
  text-align: center;
}

::v-deep {
  .line-chart {
    &__tooltip {
      transition: opacity 1s;
      rect,
      path {
        fill: $main-color;
      }
    }

    &__line {
      stroke: $stroke-color;
      fill: none;
      stroke-width: 1.5;
    }

    &__circle {
      fill: white;
      stroke: $main-color;
      stroke-width: 1;

      &:hover {
        fill: $main-color;
        stroke: white;
      }
    }

    .path {
      stroke-dasharray: 2000;
    }

    .line-enter-active {
      transition: all 1s linear;
      stroke-dashoffset: 0;
    }

    .line-enter,
    .line-leave-to {
      stroke-dashoffset: 2000;
    }

    .hide {
      opacity: 0;
      pointer-events: none;
    }

    .domain {
      display: none;
    }

    .axis {
      font-size: 0.4rem;
    }
  }

  .tick {
    &:not(:first-of-type) line {
      stroke-opacity: 0.3;
    }
  }
}
</style>
